import { z } from 'zod';
import { baseModelSchema } from '../../../model/baseModel';
import { ISalesPointFormValues } from './salesPointFormValues';

/**
 * Zod schema for validating sales point data.
 * Extends the base model schema with sales point-specific fields.
 * Represents a point of sale location in the system.
 *
 * @property {string} name - Name of the sales point (max 255 characters)
 * @property {number[]} farmIds - Array of farm IDs associated with this sales point
 */
export const salesPointSchema = baseModelSchema.extend({
    name: z.string().nonempty('Name is required').max(255, 'Name can only be up to 255 characters long.').default(''),
    farmIds: z.array(z.number()).default([]),
});

/**
 * TypeScript interface representing a SalesPoint object.
 * Generated from the Zod schema for type safety.
 */
export type ISalesPoint = z.infer<typeof salesPointSchema>;

/**
 * Creates a validated SalesPoint object from form values.
 *
 * @param {ISalesPointFormValues} formValues - The form data to convert
 * @returns {ISalesPoint} A validated sales point object
 * @throws {ZodError} If validation fails
 */
export function SalesPoint(formValues : ISalesPointFormValues) : ISalesPoint {
    const { farms } = formValues;
    return salesPointSchema.parse({
        ...formValues,
        farmIds: farms?.map(x => Number(x.value)) ?? [],
    });
}
